import * as React from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { Avatar, BuildTag, Button, Icon, Lockup, Text, type IconName } from '../../ui/index.js';
import { useAccess, useNotifications } from '../../api/hooks.js';
import { capabilities, isNative } from '../../platform/index.js';
import { Focusable, FocusZone } from '../focus/Focusable.js';
import { HintsBar } from '../focus/HintsBar.js';
import {
  useGamepadNavigation,
  useGamepadPaging,
  useGamepadScrolling,
  useGamepadButton,
} from '../focus/useGamepad.js';
import { isTextEntryNode, leaveTextEntry } from '../focus/textEntry.js';
import { lastInputSource } from '../../gamepad/input.js';
import { SiteFooter } from './SiteFooter.js';
import { UpdateRequiredGate } from './UpdateRequiredGate.js';
import { UpdateBanner } from './UpdateBanner.js';
import { Wallpaper } from './Wallpaper.js';
import { TOP_BAR_SLOT } from './ScreenChrome.js';
import { useOsNotifications } from '../../notifications/useOsNotifications.js';
import { startAppUpdates } from '../../lib/appUpdate.js';
import { useAppVersion } from '../../lib/appVersion.js';
import { syncTray } from '../../lib/tray.js';
import { useSettings } from '../../lib/settings.js';
import { startBigPicture, toggleBigPicture, useBigPicture } from '../../lib/bigPicture.js';
import { onNative } from '../../platform/tauri.js';
import { useDownloads } from '../../downloads/store.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Signed in, whether or not they pay for anything. */
  requiresSession?: boolean;
  badge?: number;
}

/**
 * The rail from the brand board, in its order.
 *
 * Everything here is browsable by anyone. Nothing in this rail is behind a membership: what a
 * membership buys is the Play button and the Discord role, and each of those sells itself
 * where it stands.
 *
 * Games and Library are the console distinction the board draws — everything we make, and the
 * part of it that is on this machine — and they are two screens rather than one filtered two
 * ways. Games is for choosing a game and Library is for playing one, which is a different
 * screen's worth of behaviour rather than a different list.
 */
const PLACES: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/games', label: 'Games', icon: 'games' },
  { to: '/library', label: 'Library', icon: 'library' },
  { to: '/studio', label: 'Studio', icon: 'studio' },
];

/** The three the board puts under a thumb. */
const TABS: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/games', label: 'Games', icon: 'games' },
  { to: '/studio', label: 'Studio', icon: 'studio' },
];

/**
 * The frame every screen sits in: GOOD BUILD OS (`03-client-app.md` section 4).
 *
 * It owns the four things that must exist on every screen — the rail, the top bar, the
 * controller hints and the global gamepad bindings — and nothing else. Screens render their
 * own content and never draw their own chrome.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const { me, signedIn, hasAccess, isAdmin } = useAccess();
  const notifications = useNotifications();
  const downloads = useDownloads();
  const { closeToTray } = useSettings();
  const bigPicture = useBigPicture();

  useGamepadNavigation();
  useGamepadScrolling();
  useGamepadPaging();
  useOsNotifications();

  // Start opens the main menu, Select opens notifications (section 3.3).
  useGamepadButton('start', () => void navigate({ to: signedIn ? '/account' : '/' }));
  useGamepadButton('select', () => void navigate({ to: '/notifications' }), signedIn);

  /**
   * Focus, put back on the screen.
   *
   * A screen with something to choose gets the first of them. A screen with nothing to choose —
   * an audit table, a storage report — gets the scroll area itself, so arrows and the page keys
   * still move it. Without that, a screen that is only there to be read is a screen a keyboard
   * cannot read past the fold.
   */
  const focusScreen = React.useCallback(() => {
    const main = document.querySelector<HTMLElement>('.gb-os__main');
    const target = main?.querySelector('.gb-focusable:not(.gb-focusable--disabled)');
    if (target) void setFocus('screen');
    else main?.focus();
  }, []);

  /** Back, from whichever button asked for it. An overlay owns its own way out. */
  const goBack = React.useCallback(() => {
    if (document.querySelector('.gb-sheet-backdrop')) return;
    // A caret in a box is the innermost thing on the screen, so it is the first thing back
    // leaves. Without this, B out of a half-written description throws the screen away with it.
    if (leaveTextEntry()) return;
    window.history.back();
  }, []);

  // B goes back, everywhere, unless something is trapping focus.
  useGamepadButton('b', goBack);
  // The shoulder button that is not already taken by a filter strip: Y opens search, which is
  // the one destination a controller cannot otherwise reach quickly.
  useGamepadButton('y', () => void navigate({ to: '/search' }));

  // The keyboard gets the same two bindings the controller has, because a keyboard is how this
  // app is used on a desk and B has no key. Escape is back; a text field keeps its own Escape,
  // which blurs the field and marks the event handled, so the second press lands here.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (isTextEntryNode(event.target)) return;
      goBack();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goBack]);

  /**
   * Every screen opens with something focused.
   *
   * A screen that autofocuses nothing is a dead end for a controller: there is nowhere for a
   * direction to move from, so nothing moves at all. Screens still say where focus should land
   * with `autoFocus`; this runs after the screen has had its chance, and only picks up the ones
   * that did not take it.
   *
   * Only for a controller or a keyboard. A pointer never needs focus moved for it, and moving
   * it would scroll a page somebody is reading with the wheel.
   */
  React.useEffect(() => {
    if (lastInputSource() === 'pointer') return;
    const timer = setTimeout(() => {
      if (document.querySelector('.gb-os__main [data-focused="true"]')) return;
      focusScreen();
    }, 120);
    return () => clearTimeout(timer);
    // `focusScreen` is a useCallback with no deps, so listing it changes nothing at runtime;
    // the route is still the only thing that re-runs this.
  }, [location.pathname, focusScreen]);

  /**
   * The floor under all of it: a direction always has somewhere to move from.
   *
   * A screen that arrives after its own autofocus has run — a slow list, a query that was
   * still loading — can still end up with nothing focused, and at that point the stick is
   * dead and there is no way to tell why. So the first direction or A press with nothing
   * focused spends itself putting focus on the screen, and the next one moves.
   *
   * This sits on the same key events the library listens to and the gamepad is turned into,
   * so it covers a controller and a keyboard with one rule (`03-client-app.md` section 3.2).
   */
  React.useEffect(() => {
    const KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter']);
    const onKeyDown = (event: KeyboardEvent) => {
      if (!KEYS.has(event.key)) return;
      if (document.querySelector('[data-focused="true"]')) return;
      const node = event.target as HTMLElement | null;
      const tag = node?.tagName;
      if (node?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }
      focusScreen();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusScreen]);

  /**
   * Big Picture, from launch (`03-client-app.md` section 4.6).
   *
   * It runs before the first screen has settled rather than after, so a Steam Deck coming up in
   * Gaming Mode is already the right size by the time there is anything on it to read. The mode
   * owns its own launch default, the pointer and the window; the shell only starts it.
   */
  React.useEffect(() => startBigPicture(), []);

  /**
   * F11, which is what a full screen is bound to everywhere else.
   *
   * The controller reaches this through the top bar and `/settings` reaches it for good. This is
   * the one for a keyboard, and it is the way out when the mode has taken the whole screen and
   * the top bar is not where somebody expects to look for the exit.
   */
  React.useEffect(() => {
    if (!capabilities.bigPicture) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'F11' || event.defaultPrevented) return;
      // The browser and the window manager both bind this; ours is the one that runs.
      event.preventDefault();
      toggleBigPicture();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // The app keeps itself current: at launch and every half hour after it
  // (`04-launcher-and-patcher.md` section 1.2). Nothing is installed without being asked.
  React.useEffect(() => startAppUpdates(), []);

  // What the window's close button does (`04-launcher-and-patcher.md` section 1.3). The shell
  // has to be told before anybody can press it, and told again whenever the switch moves.
  React.useEffect(() => {
    void syncTray(closeToTray);
  }, [closeToTray]);

  // Downloads are why the app is still running with its window closed, so the tray offers a way
  // straight to them. The shell cannot navigate a router it does not know about, so it asks.
  React.useEffect(() => {
    let stop: (() => void) | undefined;
    let gone = false;
    void onNative('tray://navigate', (to) => void navigate({ to })).then((off) => {
      if (gone) off();
      else stop = off;
    });
    return () => {
      gone = true;
      stop?.();
    };
  }, [navigate]);

  const unread = notifications.data?.unreadCount ?? 0;

  // Downloads sit with Home, Games and Library rather than down with Settings: a 30 GB build
  // takes long enough that "how is it going" is a thing someone comes back to ask, and the
  // badge is the answer before they open it.
  const installsGames = capabilities.installWindowsGames || capabilities.installAndroidGames;
  const downloadsItem: NavItem = {
    to: '/downloads',
    label: 'Downloads',
    icon: 'download',
    badge: downloads.queue.length,
  };
  // A browser installs nothing, so it has no shelf of its own: Games is the whole of it there,
  // and a Library entry would lead to a room that cannot be filled from that machine.
  const places: NavItem[] = installsGames
    ? [...PLACES, downloadsItem]
    : PLACES.filter((item) => item.to !== '/library');
  // A phone has no rail, so the tab bar is the whole of its navigation. Leaving Downloads out
  // of it would put the queue behind a button on a game page on the one device where a download
  // is most likely to be interrupted.
  const tabs: NavItem[] = installsGames ? [...TABS, downloadsItem] : TABS;

  const account: NavItem[] = [
    // Free for anyone signed in: a membership buys the Play button, not the right to write a
    // mod for a game you already own (`03-client-app.md` section 8).
    ...(signedIn ? [{ to: '/modding', label: 'Modding', icon: 'code' as const }] : []),
    ...(isAdmin ? [{ to: '/admin', label: 'Admin', icon: 'admin' as const }] : []),
    ...(isNative ? [] : [{ to: '/download', label: 'Get the app', icon: 'download' as const }]),
    { to: '/search', label: 'Search', icon: 'search' },
    { to: '/settings', label: 'Settings', icon: 'settings' },
  ];

  // A prefix match has to stop at a path boundary, or `/download` lights up on `/downloads` and
  // the rail shows two destinations as the one you are on. Games keeps the light while a game's
  // own page is open, because that page is where this rail entry leads.
  const isActive = (item: NavItem): boolean => {
    const { pathname } = location;
    return item.to === '/'
      ? pathname === '/'
      : pathname === item.to || pathname.startsWith(`${item.to}/`);
  };

  return (
    <div className="gb-os">
      {/* The room this console sits in: the screen's own artwork, blurred back behind
          everything. It is the first child so it is painted before any of the chrome. */}
      <Wallpaper />

      <a className="gb-skip" href="#gb-main">
        {'Skip to the screen'}
      </a>

      {/* The launch offer, across the top of the rail and the bar both. It is the one temporary
          thing in this frame, so it sits above the navigation rather than inside it, and it
          renders nothing at all once it is over or once the reader has paid. */}

      <FocusZone focusKey="rail" direction="column" className="gb-rail">
        <Link to="/" className="gb-rail__brand" aria-label="GOOD BUILD OS, home">
          <Lockup product="OS" />
        </Link>

        <div className="gb-rail__group">
          {places.map((item) => (
            <RailLink key={`${item.to}-${item.label}`} item={item} active={isActive(item)} />
          ))}
        </div>

        <span className="gb-rail__divider" aria-hidden="true" />

        <div className="gb-rail__group">
          {account.map((item) => (
            <RailLink key={`${item.to}-${item.label}`} item={item} active={isActive(item)} />
          ))}
        </div>

        <RailBuild />
      </FocusZone>

      <FocusZone focusKey="topbar" direction="row" className="gb-topbar">
        {/* The lockup lives at the top of the rail on a desktop; on a phone there is no rail,
            so it comes back here. */}
        <Link to="/" className="gb-topbar__brand" aria-label="GOOD BUILD, home">
          <Lockup />
        </Link>

        {/* Where the open screen puts its own switch, so a screen with two halves does not spend
            a row of itself saying which one you are looking at (`ScreenChrome.tsx`). */}
        <div className="gb-topbar__screen" id={TOP_BAR_SLOT} />

        <span className="gb-topbar__spacer" />

        {/* The only thing in this bar that is not navigation: the one action we want.

            It goes to `/join` signed in or not. The price page is readable by a visitor, so
            sending them to a sign in screen first would be asking for an account before saying
            what it costs; `/join` asks for the sign in at the point of paying instead. */}
        {!hasAccess ? (
          <Focusable
            onSelect={() => void navigate({ to: '/join' })}
            selectLabel="Become a supporter"
            focusKey="topbar-supporter"
            className="gb-focusable--flat gb-topbar__cta"
          >
            <Button size="2">{'Become a supporter'}</Button>
          </Focusable>
        ) : null}

        {/* The way in and the way out, in the one place that is on every screen. A mode that
            changes the whole window has to have its exit somewhere fixed, and a controller
            reaches the top bar from anywhere. */}
        {capabilities.bigPicture ? (
          <Focusable
            onSelect={toggleBigPicture}
            selectLabel={bigPicture ? 'Leave Big Picture' : 'Big Picture'}
            focusKey="topbar-bigpicture"
            className="gb-focusable--flat gb-topbar__slot"
          >
            <button
              type="button"
              className="gb-topbar__action gb-topbar__mode"
              data-on={bigPicture}
              aria-pressed={bigPicture}
              aria-label={bigPicture ? 'Leave Big Picture' : 'Big Picture'}
              title={bigPicture ? 'Leave Big Picture (F11)' : 'Big Picture (F11)'}
            >
              <Icon name="tv" size={20} />
            </button>
          </Focusable>
        ) : null}

        <Focusable
          onSelect={() => void navigate({ to: '/search' })}
          selectLabel="Search"
          focusKey="topbar-search"
          className="gb-focusable--flat gb-topbar__slot"
        >
          <Link to="/search" className="gb-topbar__action" aria-label="Search">
            <Icon name="search" size={20} />
          </Link>
        </Focusable>

        {signedIn ? (
          <Focusable
            onSelect={() => void navigate({ to: '/notifications' })}
            selectLabel="Notifications"
            focusKey="topbar-bell"
            className="gb-focusable--flat gb-topbar__slot"
          >
            <Link
              to="/notifications"
              className="gb-topbar__action"
              aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            >
              <Icon name="bell" size={20} />
              {unread > 0 ? (
                <span className="gb-topbar__count">{unread > 99 ? '99+' : unread}</span>
              ) : null}
            </Link>
          </Focusable>
        ) : null}

        {signedIn && me ? (
          <Focusable
            onSelect={() => void navigate({ to: '/account' })}
            selectLabel="Account"
            focusKey="topbar-account"
            className="gb-focusable--flat gb-topbar__slot"
          >
            <Link to="/account" className="gb-topbar__account">
              <Avatar
                size="2"
                src={me.user.avatarUrl ?? undefined}
                fallback={(me.user.displayName ?? me.user.username).slice(0, 1)}
              />
              <span className="gb-topbar__name">{me.user.displayName ?? me.user.username}</span>
            </Link>
          </Focusable>
        ) : (
          <Focusable
            onSelect={() => void navigate({ to: '/signin' })}
            selectLabel="Sign in"
            focusKey="topbar-signin"
            className="gb-focusable--flat"
          >
            <Button size="2" variant="ghost">
              {'Sign in'}
            </Button>
          </Focusable>
        )}

        <Clock />
      </FocusZone>

      {/* Focusable only so it can be given the keyboard when the screen it holds has nothing
          of its own to focus. It is never in the tab order. */}
      <main className="gb-os__main" id="gb-main" tabIndex={-1}>
        {/* One zone around whatever screen is open, so focus can always be handed back to
            "the screen" without the shell knowing which screen that is. */}
        <FocusZone focusKey="screen" direction="column" preserve={false} className="gb-os__page">
          {/* A version below the minimum in `/app/config` blocks everything until it updates. */}
          {capabilities.osNotifications ? <UpdateRequiredGate /> : null}
          {children}
          {/* The website's foot: support address, both legal pages, and who is actually being
              paid. Inside the page rather than the shell grid, so it scrolls away with the
              screen instead of eating a strip of a console that has none to spare. Web only —
              see `SiteFooter`. */}
          {isNative ? null : <SiteFooter />}
        </FocusZone>
      </main>

      {/* A version that is merely newer waits for a restart somebody asks for. It sits outside
          the screen so it is never what focus lands on when a screen opens: the A button a
          moment after navigating belongs to the screen, not to a restart. */}
      <UpdateBanner />

      <nav className="gb-tabbar" aria-label="Sections">
        {tabs.map((item) => (
          <Link key={item.to} to={item.to} className="gb-tabbar__item" data-active={isActive(item)}>
            <span className="gb-tabbar__icon">
              <Icon name={item.icon} size={22} />
              {item.badge && item.badge > 0 ? (
                <span className="gb-tabbar__count">{item.badge > 9 ? '9+' : item.badge}</span>
              ) : null}
            </span>
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>

      <HintsBar />
    </div>
  );
}

function RailLink({ item, active }: { item: NavItem; active: boolean }) {
  const navigate = useNavigate();
  return (
    <Focusable
      onSelect={() => void navigate({ to: item.to })}
      selectLabel={item.label}
      focusKey={`rail-${item.label}`}
      className="gb-focusable--flat"
    >
      <Link to={item.to} className="gb-rail__item" data-active={active} title={item.label}>
        <span className="gb-rail__icon">
          <Icon name={item.icon} size={20} />
        </span>
        <span className="gb-rail__label">{item.label}</span>
        {item.badge && item.badge > 0 ? (
          <span className="gb-rail__count">{item.badge > 9 ? '9+' : item.badge}</span>
        ) : null}
      </Link>
    </Focusable>
  );
}

/**
 * The version at the bottom of the rail, where the manifesto used to be.
 *
 * It is there to be read out in a bug report, so it is the version of the app that is running
 * rather than the one this frontend was built with — on the desktop those come apart the moment
 * an update installs itself.
 */
function RailBuild() {
  const version = useAppVersion();
  return <BuildTag version={version} className="gb-rail__build" />;
}

/**
 * The clock on the board's top bar.
 *
 * It is the one thing up there that is not navigation, and it is most of what makes the app
 * read as a console rather than as a web page. It ticks on the minute, not every second.
 */
function Clock() {
  const [now, setNow] = React.useState(() => new Date());

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const msToNextMinute = 60_000 - (Date.now() % 60_000);
      timer = setTimeout(() => {
        setNow(new Date());
        schedule();
      }, msToNextMinute + 50);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  const label = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return (
    <Text className="gb-topbar__clock" render={<time dateTime={now.toISOString()} />}>
      {label}
    </Text>
  );
}
